import { View, Text, styled, ScrollView } from 'tamagui'
import { Copy, Trash2, Plus } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { invites as mockInvites } from '@/mocks/invites'
import { users } from '@/mocks'
import type { Invite } from '@/types'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 8, maxWidth: 700 })

const InviteRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12,
  borderRadius: '$borderRadiusLg', borderWidth: 1, borderColor: '$borderDefault', gap: 12,
})

const InviteCode = styled(Text, {
  fontFamily: '$fontMono', fontSize: 14, fontWeight: '600', color: '$accent',
})

const InviteMeta = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, color: '$sidebarMuted',
})

const IconButton = styled(View, {
  width: 32, height: 32, borderRadius: '$borderRadiusMd', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', hoverStyle: { backgroundColor: '$sidebarHover' },
})

const CreateButton = styled(View, {
  height: 36, paddingHorizontal: 16, borderRadius: '$borderRadiusMd',
  alignItems: 'center', justifyContent: 'center', backgroundColor: '$accent',
  cursor: 'pointer', flexDirection: 'row', gap: 6,
  hoverStyle: { opacity: 0.9 },
})

function formatExpiry(ts: number | null): string {
  if (!ts) return 'Never'
  const diff = ts - Date.now()
  if (diff <= 0) return 'Expired'
  const days = Math.ceil(diff / 86400000)
  return `${days}d remaining`
}

export function InviteSettings() {
  const { activeServerId } = useAppStore()
  const [localInvites, setLocalInvites] = useState<Invite[]>(
    mockInvites.filter(inv => inv.serverId === activeServerId)
  )
  const [copied, setCopied] = useState<string | null>(null)

  const handleCopy = (code: string) => {
    navigator.clipboard?.writeText(code)
    setCopied(code)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleRevoke = (inviteId: string) => {
    setLocalInvites(prev => prev.filter(inv => inv.id !== inviteId))
  }

  const handleCreate = () => {
    const newInvite: Invite = {
      id: `inv-${Date.now()}`,
      serverId: activeServerId || '',
      code: `invite-${Math.random().toString(36).slice(2, 8)}`,
      createdBy: 'u1',
      createdAt: Date.now(),
      expiresAt: Date.now() + 86400000 * 7,
      maxUses: null,
      uses: 0,
    }
    setLocalInvites(prev => [newInvite, ...prev])
  }

  return (
    <Container>
      <Header>
        <Title>Invites</Title>
        <CreateButton onPress={handleCreate}>
          <Plus size={14} color="$surface" />
          <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Create Invite</Text>
        </CreateButton>
      </Header>
      <ScrollView flex={1}>
        <Body>
          {localInvites.length === 0 && (
            <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
              No active invites. Create one to invite people to this server.
            </Text>
          )}
          {localInvites.map((inv) => {
            const creator = users[inv.createdBy]
            return (
              <InviteRow key={inv.id}>
                <View flex={1} gap={4}>
                  <InviteCode>{inv.code}</InviteCode>
                  <View flexDirection="row" gap={12}>
                    <InviteMeta>by {creator?.displayName || 'Unknown'}</InviteMeta>
                    <InviteMeta>{inv.uses} uses{inv.maxUses ? ` / ${inv.maxUses}` : ''}</InviteMeta>
                    <InviteMeta>{formatExpiry(inv.expiresAt)}</InviteMeta>
                  </View>
                </View>
                <IconButton onPress={() => handleCopy(inv.code)}>
                  <Copy size={14} color={copied === inv.code ? '$accent' : '$sidebarMuted'} />
                </IconButton>
                <IconButton onPress={() => handleRevoke(inv.id)}>
                  <Trash2 size={14} color="$error" />
                </IconButton>
              </InviteRow>
            )
          })}
        </Body>
      </ScrollView>
    </Container>
  )
}
